import type { StateCreator } from 'zustand'
import {
  listAgentRuns,
  getAgentRun,
  triggerAgentRun,
  AgentRunConflictError,
  type AgentRun,
  type AgentRunDetail,
} from '@/shared/lib/api'

const MAX_RUNS = 100

export interface AgentRunsSlice {
  agentRuns: AgentRun[]
  agentRunsLoading: boolean
  agentRunsError: string | null
  selectedAgentRunDetail: AgentRunDetail | null

  fetchAgentRuns: (params?: { agent_name?: string; limit?: number }) => Promise<void>
  fetchAgentRunDetail: (id: number) => Promise<void>
  clearAgentRunDetail: () => void
  triggerAgentRun: (agentName: string) => Promise<void>
}

export const createAgentRunsSlice: StateCreator<
  AgentRunsSlice,
  [],
  [],
  AgentRunsSlice
> = (set, get) => ({
  agentRuns: [],
  agentRunsLoading: false,
  agentRunsError: null,
  selectedAgentRunDetail: null,

  fetchAgentRuns: async (params) => {
    set({ agentRunsLoading: true, agentRunsError: null })
    try {
      const runs = await listAgentRuns({ limit: MAX_RUNS, ...params })
      set({ agentRuns: runs.slice(0, MAX_RUNS), agentRunsLoading: false })
    } catch (err) {
      set({
        agentRunsError:
          err instanceof Error ? err.message : 'Failed to fetch agent runs',
        agentRunsLoading: false,
      })
    }
  },

  fetchAgentRunDetail: async (id) => {
    try {
      const detail = await getAgentRun(id)
      set({ selectedAgentRunDetail: detail })
    } catch (err) {
      set({
        agentRunsError:
          err instanceof Error ? err.message : 'Failed to fetch agent run detail',
      })
    }
  },

  clearAgentRunDetail: () => {
    set({ selectedAgentRunDetail: null })
  },

  triggerAgentRun: async (agentName) => {
    // Optimistic: insert a placeholder run with status="running"
    const placeholder: AgentRun = {
      id: -Date.now(), // temporary negative id to avoid collision
      workspace_id: 0,
      schedule_id: null,
      agent_name: agentName,
      trigger: 'manual',
      status: 'running',
      started_at: new Date().toISOString(),
      completed_at: null,
      prompt: '',
      reasoning: null,
      tool_calls: [],
      created_workflow_ids: [],
      error: null,
      session_id: null,
    }
    set({ agentRuns: [placeholder, ...get().agentRuns].slice(0, MAX_RUNS) })

    try {
      await triggerAgentRun(agentName)
      // Refetch to replace placeholder with real run
      await get().fetchAgentRuns()
    } catch (err) {
      // Remove placeholder on error
      set({
        agentRuns: get().agentRuns.filter((r) => r.id !== placeholder.id),
      })
      if (err instanceof AgentRunConflictError) {
        throw err
      }
      set({
        agentRunsError:
          err instanceof Error ? err.message : 'Failed to trigger agent run',
      })
      throw err
    }
  },
})
